import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Dashboard from './components/Dashboard';
import Login from './components/Login';
import Analysis from './components/Analysis';
import Summary from './components/Summary';
import Team from './components/Team';
import Valuation from './components/Valuation';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import './App.css';
import { DashboardHeader } from './components/DashboardHeader';
import { CompanyProvider } from '@/context/CompanyContext';
import MarketResearch from './components/MarketResearch';
import WebRTCInterview from './components/WebRTCInterview';
import { InvestmentMemo } from "@/components/InvestmentMemo";
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Billing from './pages/Billing';
import BillingSuccess from './pages/BillingSuccess';
import BillingCancel from './pages/BillingCancel';
import Profile from './pages/Profile';
import Home from './pages/Home';
import PresentationAnalysis from './components/PresentationAnalysis';
import DataroomRedesigned from './pages/DataroomRedesigned';
import { initPostHog } from './analytics/posthog';
import { usePageTracking } from './hooks/usePageTracking';

// Protected Route component
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  const isDashboardPage = location.pathname === '/dashboard';
  
  // Don't redirect while checking authentication
  if (isLoading) {
    return <div>Loading...</div>;  // Or your loading component
  }

  // If we're not authenticated, redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  return (
    <>
      <DashboardHeader isDashboardPage={isDashboardPage} />
      {children}
    </>
  );
};

// Login Route component
const LoginRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isLoading } = useAuth();

  // Don't redirect while checking authentication
  if (isLoading) {
    return <div>Loading...</div>;  // Or your loading component
  }

  // If we're authenticated, redirect to home
  if (isAuthenticated) {
    return <Navigate to="/home" replace />;
  }

  return <>{children}</>;
};

const AppContent = () => {
  const location = useLocation();
  
  // Track page views
  usePageTracking();
  
  return (
    <div className="min-h-screen">
      <main className={`min-h-screen bg-background/50 text-foreground relative p-6 ${location.pathname !== '/dashboard' && location.pathname !== '/profile' && location.pathname !== '/billing' && location.pathname !== '/home' ? 'ml-64' : ''}`}>
        <Routes>
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route 
            path="/login" 
            element={
              <LoginRoute>
                <Login />
              </LoginRoute>
            } 
          />
          <Route path="/home" element={
            <ProtectedRoute>
              <Home />
            </ProtectedRoute>
          } />
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          } />
          <Route path="/analysis/:id" element={
            <ProtectedRoute>
              <Analysis />
            </ProtectedRoute>
          } />
          <Route path="/summary/:id" element={
            <ProtectedRoute>
              <Summary />
            </ProtectedRoute>
          } />
          <Route path="/valuation/:id" element={
            <ProtectedRoute>
              <Valuation />
            </ProtectedRoute>
          } />
          <Route path="/founders/:id" element={
            <ProtectedRoute>
              <Team />
            </ProtectedRoute>
          } />
          <Route path="/market-research/:id" element={
            <ProtectedRoute>
              <MarketResearch />
            </ProtectedRoute>
          } />
          <Route path="/interview/:id" element={
            <ProtectedRoute>
              <WebRTCInterview />
            </ProtectedRoute>
          } />
          <Route path="/investment-memo/:id" element={
            <ProtectedRoute>
              <InvestmentMemo />
            </ProtectedRoute>
          } />
          <Route path="/presentation-analysis/:id" element={
            <ProtectedRoute>
              <PresentationAnalysis />
            </ProtectedRoute>
          } />
          <Route path="/billing" element={
            <ProtectedRoute>
              <Billing />
            </ProtectedRoute>
          } />
          <Route path="/billing/success" element={
            <ProtectedRoute>
              <BillingSuccess />
            </ProtectedRoute>
          } />
          <Route path="/billing/cancel" element={
            <ProtectedRoute>
              <BillingCancel />
            </ProtectedRoute>
          } />
          <Route path="/profile" element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          } />
          <Route path="/dataroom" element={
            <ProtectedRoute>
              <DataroomRedesigned />
            </ProtectedRoute>
          } />
          <Route path="/" element={<Navigate to="/home" replace />} />
        </Routes>
      </main>
    </div>
  );
};

function App() {
  useEffect(() => {
    // Initialize PostHog analytics
    initPostHog();
  }, []);

  return (
    <AuthProvider>
      <ThemeProvider>
        <CompanyProvider>
          <Router>
            <AppContent />
          </Router>
        </CompanyProvider>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;
